import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../Icon/Icon';

function Lightbox({ open, images = [], index = 0, src = '', alt, onClose }) {
    const [mounted, setMounted] = useState(false);
    const [current, setCurrent] = useState(index);

    const list = useMemo(
        () => (images?.length ? images : src ? [src] : []),
        [images, src]
    );

    useEffect(() => setMounted(true), []);
    useEffect(() => {
        if (open) setCurrent(index);
    }, [open, index]);

    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    const hasPrev = list.length > 1 && current > 0;
    const hasNext = list.length > 1 && current < list.length - 1;
    const goPrev = useCallback(() => {
        if (hasPrev) setCurrent((c) => c - 1);
    }, [hasPrev]);
    const goNext = useCallback(() => {
        if (hasNext) setCurrent((c) => c + 1);
    }, [hasNext]);

    const handleKey = useCallback(
        (e) => {
            if (!open) return;
            if (e.key === 'Escape') onClose?.();
            if (e.key === 'ArrowLeft') goPrev();
            if (e.key === 'ArrowRight') goNext();
        },
        [open, goPrev, goNext, onClose]
    );

    useEffect(() => {
        if (!open) return;
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [open, handleKey]);

    if (!mounted || !open || list.length === 0) return null;

    const backdropStyle = {
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
    };

    const overlay = (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
            style={backdropStyle}
            role="dialog"
            aria-modal="true"
            aria-label={alt || 'Lightbox image view'}
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose?.();
            }}>
            {list.length > 1 && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        goPrev();
                    }}
                    disabled={!hasPrev}
                    aria-label="Anterior"
                    className={`
                        absolute left-4 top-1/2 -translate-y-1/2
                        rounded-full p-3 bg-white/95 shadow-lg ring-1 ring-black ring-opacity-20
                        hover:bg-black hover:bg-opacity-20 transition-all focus:outline-none focus:ring-2 focus:ring-gray-900
                        ${
                            hasPrev
                                ? 'opacity-100'
                                : 'opacity-0 pointer-events-none'
                        }
                    `}>
                    <Icon
                        name="chevron-left"
                        className="h-5 w-5 text-gray-900"
                    />
                </button>
            )}

            <div
                className="relative rounded-xl shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}>
                <img
                    src={list[current]}
                    alt={alt}
                    className="rounded-xl shadow-2xl ring-1 ring-black/10 bg-black/5 w-full max-h-screen object-contain"
                    draggable={false}
                />

                <button
                    type="button"
                    onClick={onClose}
                    className="
                        absolute top-2 right-2
                        inline-flex items-center justify-center
                        rounded-full
                        bg-black/60 text-white
                        h-8 w-8
                        shadow-lg hover:bg-black/80
                        focus:outline-none focus:ring-2 focus:ring-white
                    "
                    aria-label="close">
                    <Icon
                        name="times"
                        className="h-4 w-4"
                    />
                </button>
            </div>

            {list.length > 1 && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        goNext();
                    }}
                    disabled={!hasNext}
                    aria-label="Siguiente"
                    className={`
                        absolute right-4 top-1/2 -translate-y-1/2
                        rounded-full p-3 bg-white/95 shadow-lg ring-1 ring-black ring-opacity-20
                        hover:bg-black hover:bg-opacity-20 transition-all focus:outline-none focus:ring-2 focus:ring-gray-900
                        ${
                            hasNext
                                ? 'opacity-100'
                                : 'opacity-0 pointer-events-none'
                        }
                    `}>
                    <Icon
                        name="chevron-right"
                        className="h-5 w-5 text-gray-900"
                    />
                </button>
            )}

            {list.length > 1 && (
                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 px-4 py-2 bg-black bg-opacity-50 rounded-full text-white text-sm font-medium">
                    {current + 1} / {list.length}
                </div>
            )}
        </div>
    );

    return createPortal(overlay, document.body);
}

export default Lightbox;
